import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["digit", "hiddenInput"]

  connect() {
    if (this.digitTargets.length > 0) {
      this.digitTargets[0].focus()
      this.digitTargets[0].select()
    }
  }

  handleInput(event) {
    const input = event.target
    const index = parseInt(input.dataset.index)

    // Strip non-digits
    const digits = input.value.replace(/\D/g, "")

    if (digits.length === 0) {
      input.value = ""
      this.updateHiddenInput()
      return
    }

    // If multiple digits typed/pasted into one field, distribute them
    if (digits.length > 1) {
      this.fillFrom(index, digits)
      return
    }

    // Single digit
    input.value = digits

    this.updateHiddenInput()

    // Auto-advance to next input
    if (index < this.digitTargets.length - 1) {
      this.digitTargets[index + 1].focus()
      this.digitTargets[index + 1].select()
    }

    if (this.isComplete()) {
      this.submitForm()
    }
  }

  handleKeydown(event) {
    const input = event.target
    const index = parseInt(input.dataset.index)

    if (event.key === "Backspace") {
      if (input.value) {
        // Clear current input, stay here
        input.value = ""
        this.updateHiddenInput()
        event.preventDefault()
      } else if (index > 0) {
        // Move back and clear previous
        event.preventDefault()
        const prev = this.digitTargets[index - 1]
        prev.value = ""
        prev.focus()
        prev.select()
        this.updateHiddenInput()
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault()
      this.digitTargets[index - 1].focus()
      this.digitTargets[index - 1].select()
    } else if (event.key === "ArrowRight" && index < this.digitTargets.length - 1) {
      event.preventDefault()
      this.digitTargets[index + 1].focus()
      this.digitTargets[index + 1].select()
    } else if (/^\d$/.test(event.key)) {
      // Digit key pressed — if current input already has a value,
      // replace it and advance (prevents maxlength from swallowing the keystroke)
      event.preventDefault()
      input.value = event.key
      this.updateHiddenInput()

      if (index < this.digitTargets.length - 1) {
        this.digitTargets[index + 1].focus()
        this.digitTargets[index + 1].select()
      }

      if (this.isComplete()) {
        this.submitForm()
      }
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const pastedData = event.clipboardData.getData("text")
    const digits = pastedData.replace(/\D/g, "")

    if (digits.length === 0) return

    const index = parseInt(event.target.dataset.index)
    this.fillFrom(index, digits)
  }

  fillFrom(startIndex, digits) {
    const chars = digits.slice(0, this.digitTargets.length - startIndex).split("")

    chars.forEach((digit, i) => {
      const target = this.digitTargets[startIndex + i]
      if (target) {
        target.value = digit
      }
    })

    this.updateHiddenInput()

    // Focus the next empty input, or the last filled one
    const nextEmpty = this.digitTargets.findIndex(input => !input.value)
    if (nextEmpty !== -1) {
      this.digitTargets[nextEmpty].focus()
      this.digitTargets[nextEmpty].select()
    } else {
      this.digitTargets[this.digitTargets.length - 1].focus()
    }

    if (this.isComplete()) {
      this.submitForm()
    }
  }

  updateHiddenInput() {
    if (this.hasHiddenInputTarget) {
      const code = this.digitTargets.map(input => input.value).join("")
      this.hiddenInputTarget.value = code
    }
  }

  isComplete() {
    return this.digitTargets.every(input => /^\d$/.test(input.value))
  }

  submitForm() {
    setTimeout(() => {
      this.element.requestSubmit()
    }, 150)
  }
}
